import ShopType from "@/enum/ShopType";
import { getIsRetailer } from "@/services/api/partnership/management";
import { getIsSupplierByShopType } from "@/utils/dropShipping";
import { SupplierShopTypes } from "@/utils/ShopUtils";
import { computed, observable, makeObservable, actionBound } from "mobx";
import GlobalStore from "@/store";

class CompanyRoleStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            hasPlatformShop: computed,
            hasSupplierShop: computed,
            isRetailer: observable,
            setRetailerRole: actionBound,
            getIsRetailer: actionBound,
            isSupplier: computed,
            isMerchant: computed,
            isPureRetailer: computed,
            isPureSupplier: computed,
            isHybridSeller: computed,
            isSupplierWithPlatform: computed,
        });
    }

    /** 是否存在平台店铺 */
    get hasPlatformShop() {
        return this.globalStore.shops.some((shop) => ![...SupplierShopTypes, ShopType.MANUAL].includes(shop.type));
    }

    /** 是否存在供货商店铺 */
    get hasSupplierShop() {
        return this.globalStore.shops.some((shop) => getIsSupplierByShopType(shop.type));
    }

    /**
     * 如果需要颗粒度更细的判断，优先从组合角色标识中判断(如【库存管理】功能不提供给【纯供货商】使用)
     */

    /** ---------- 业务身份标识（兼容关系） ---------- */
    /**
     * 该标识为真时包含以下场景：
     * 1. 纯代理商（仅平台店铺且开通分销）
     * 2. 混合卖家（同时拥有供货商店铺和开通分销的平台店铺）
     *
     */
    isRetailer = false;

    setRetailerRole(isRetailer: boolean) {
        this.isRetailer = isRetailer;
    }

    async getIsRetailer() {
        this.isRetailer = await getIsRetailer();
    }

    /**
     * 该标识为真时包含以下场景：
     * 1. 纯供货商
     * 2. 混合卖家
     * 3. 供货商+基础平台商家
     * -- 等同于 hasSupplierShop
     */
    get isSupplier() {
        return this.isPureSupplier || this.isHybridSeller || this.isSupplierWithPlatform;
    }

    /** ---------- 组合角色标识（互斥关系）必定是下面的其中一种 ---------- */

    /**
     * [1] 普通发货易用户
     * - 有平台店铺
     * - 无供货商店铺
     * - 未开通分销
     */

    get isMerchant() {
        return this.hasPlatformShop && !this.hasSupplierShop && !this.isRetailer;
    }

    /**
     * [2] 纯代理商
     * - 有平台店铺
     * - 开通分销
     * - 无供货商店铺
     */
    get isPureRetailer() {
        return this.hasPlatformShop && this.isRetailer && !this.hasSupplierShop;
    }

    /**
     * [3] 纯供货商
     * - 无平台店铺
     * - 有供货商店铺
     */
    get isPureSupplier() {
        return this.hasSupplierShop && !this.hasPlatformShop;
    }

    /**
     * [4] 混合型卖家
     * - 有平台店铺
     * - 有供货商店铺
     * - 已开通分销（代理商身份）
     */
    get isHybridSeller() {
        return this.hasSupplierShop && this.hasPlatformShop && this.isRetailer;
    }

    /**
     * [5] 供货商+基础平台商家
     * - 有平台店铺
     * - 有供货商店铺
     * - 未开通分销（非代理商）
     */
    get isSupplierWithPlatform() {
        return this.hasSupplierShop && this.hasPlatformShop && !this.isRetailer;
    }
}

export default CompanyRoleStore;
